import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";
import config from "../../../aws-exports";
import dayjs from "dayjs";
import {
  fetchData,
  postQuery,
  uploadFileToS3Path,
} from "../../../services/BackendServices";
import AlertModal from "../../Requirement/components/common/AlerModel";

const KYBVerification = () => {
  const navigate = useNavigate();
  const currentDate = dayjs().format("YYYY-MM-DD");
  const bucket = process.env.REACT_APP_AMTPL_BUCKET;
  let bucketfolder = "public/media/amtpl/kybdoc";
  const filePath = `${bucketfolder}/`;
  const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${filePath}`;
  const [formData, setFormData] = useState({
    legalName: "",
    hasTradingName: false,
    tradingName: "",
    businessType: "",
    industrySector: "",
    regNumber: "",
    incorporationDate: "",
    country: "",
    taxId: "",
    registeredAddress: "",
    isOperationalDifferent: false,
    operationalAddress: "",
    websiteEmail: "",
    contactNumber: "",
    annualTurnover: "",
    paidUpCapital: "",
    bankName: "",
    accountNumber: "",
    ifscSwift: "",
    sourceOfFunds: "",
    certificateOfIncorporation: null,
    taxIdProof: null,
    lastAuditedFinancials: null,
    Status: "",
    verifyConfirm: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activeUser, setActiveUser] = useState("");
  const [countrymaster, setCountryMaster] = useState([]);
  const [businessTypesMaster, setBusinessTypesMaster] = useState([]);
  const [industrySectorsMaster, setIndustrySectorsMaster] = useState([]);
  const [sourcesOfFundsMaster, setSourcesOfFundsMaster] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userKybID, setUserKybID] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const readOnly =
    formData.Status === "Approved" ||
    (isUpdateMode &&
      formData.Status &&
      formData.Status !== "Approved" &&
      !editMode);
  const handleTabChange = (event, newValue) => setActiveTab(newValue);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  function groupByType(items) {
    return items.reduce((acc, cur) => {
      const type = cur.Type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(cur);
      return acc;
    }, {});
  }

  const loadAllDropdowns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 10000,
      });
      const items = response.items ?? [];
      const groups = groupByType(items);
      setCountryMaster(groups.Country ?? []);
      setBusinessTypesMaster(groups.businessTypes ?? []);
      setIndustrySectorsMaster(groups.industrySectors ?? []);
      setSourcesOfFundsMaster(groups.sourcesOfFunds ?? []);
    } catch (err) {
      console.error("Failed to load dropdown options:", err);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Could not load dropdown lists. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const validateField = async (name, value) => {
    let error = "";
    switch (name) {
      case "legalName":
      case "regNumber":
      case "taxId":
      case "registeredAddress":
      case "websiteEmail":
      case "contactNumber":
      case "bankName":
      case "accountNumber":
      case "ifscSwift":
      case "businessType":
      case "industrySector":
        if (!value?.trim()) error = "This field is required";
        break;
      case "verifyConfirm":
        if (!value) error = "This field is required";
        break;
      case "country":
        if (!value) error = "Please select an option";
        break;
      case "tradingName":
        if (formData.hasTradingName && !value?.trim())
          error = "This field is required";
        break;
      case "operationalAddress":
        if (formData.isOperationalDifferent && !value?.trim())
          error = "This field is required";
        break;
      case "incorporationDate":
        if (!value) error = "This field is required";
        else if (new Date(value) > new Date())
          error = "Date cannot be in the future";
        break;
      default:
        break;
    }
    return error;
  };

  const validateAllFields = async () => {
    const newErrors = {};
    const requiredFields = [
      "legalName",
      "regNumber",
      "taxId",
      "registeredAddress",
      "websiteEmail",
      "contactNumber",
      "bankName",
      "accountNumber",
      "ifscSwift",
      "businessType",
      "verifyConfirm",
      "industrySector",
      "country",
      "incorporationDate",
    ];
    for (const key of requiredFields) {
      if (formData.hasOwnProperty(key)) {
        const error = await validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    }
    if (formData.hasTradingName && formData.tradingName !== undefined) {
      const tradingError = await validateField(
        "tradingName",
        formData.tradingName
      );
      if (tradingError) newErrors.tradingName = tradingError;
    }
    if (
      formData.isOperationalDifferent &&
      formData.operationalAddress !== undefined
    ) {
      const operationalError = await validateField(
        "operationalAddress",
        formData.operationalAddress
      );
      if (operationalError) newErrors.operationalAddress = operationalError;
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleChange = async (e) => {
    if (readOnly) return;
    const { name, value, type, checked, files } = e.target;
    let newValue =
      type === "checkbox"
        ? checked
        : type === "file"
        ? files
          ? files[0]
          : null
        : value;
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: newValue };
      if (name === "legalName" && !prev.hasTradingName)
        newFormData.tradingName = newValue;
      if (name === "registeredAddress" && !prev.isOperationalDifferent)
        newFormData.operationalAddress = newValue;
      return newFormData;
    });
    if (touched[name]) {
      const fieldError = await validateField(name, newValue);
      setErrors((prev) => ({ ...prev, [name]: fieldError || undefined }));
    }
  };

  const handleBlur = async (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldError = await validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: fieldError || undefined }));
  };

  const handleFileRemove = async (name) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [name]: null }));
    if (touched[name]) {
      const fieldError = await validateField(name, null);
      setErrors((prev) => ({ ...prev, [name]: fieldError || undefined }));
    }
  };

  const fetchExistingKYB = useCallback(async () => {
    if (!activeUser) return;
    setLoading(true);
    try {
      const userKybSettings = await fetchData({
        target: "listUserKYB",
        userOrder: "UserKybID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: activeUser },
      });
      if (userKybSettings.items && userKybSettings.items.length > 0) {
        const kybData = userKybSettings.items[0];
        setIsUpdateMode(true);
        setUserKybID(kybData.UserKybID);
        setFormData({
          legalName: kybData.LegalBusinessName || "",
          hasTradingName:
            !!kybData.TradingName &&
            kybData.TradingName !== kybData.LegalBusinessName,
          tradingName: kybData.TradingName || "",
          businessType: kybData.BusinessType || "",
          industrySector: kybData.IndustrySector || "",
          regNumber: kybData.CompanyRegistrationNumber || "",
          incorporationDate: kybData.DateOfIncorporation
            ? dayjs(kybData.DateOfIncorporation).format("YYYY-MM-DD")
            : "",
          country: kybData.CountryOfIncorporation || "",
          taxId: kybData.TaxIdentificationNumber || "",
          registeredAddress: kybData.RegisteredAddress || "",
          isOperationalDifferent:
            !!kybData.OperationalAddress &&
            kybData.OperationalAddress !== kybData.RegisteredAddress,
          operationalAddress: kybData.OperationalAddress || "",
          websiteEmail: kybData.Website || "",
          contactNumber: kybData.ContactNumber || "",
          annualTurnover: kybData.AnnualTurnover?.toString() || "",
          paidUpCapital: kybData.PaidUpCapital?.toString() || "",
          bankName: kybData.BankName || "",
          accountNumber: kybData.BankAccountNumber || "",
          ifscSwift: kybData.IFSC_SwiftCode || "",
          sourceOfFunds: kybData.SourceOfFunds || "",
          certificateOfIncorporation: kybData.certificateOfIncorporation
            ? { name: kybData.certificateOfIncorporation }
            : null,
          taxIdProof: kybData.taxIdProof ? { name: kybData.taxIdProof } : null,
          lastAuditedFinancials: kybData.lastAuditedFinancials
            ? { name: kybData.lastAuditedFinancials }
            : null,
          Status: kybData.Status || "",
          verifyConfirm: true,
        });
        setEditMode(false);
      } else {
        setIsUpdateMode(false);
        setUserKybID(null);
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error fetching KYB data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Failed to load existing KYB data. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  }, [activeUser]);

  const handleSubmit = async () => {
    if (readOnly) return;
    setLoading(true);
    const validationErrors = await validateAllFields();
    setLoading(false);
    if (Object.keys(validationErrors).length > 0) {
      setModal({
        show: true,
        type: "error",
        title: "Validation Error",
        message: `Please correct the required fields.`,
        autoClose: false,
      });
      return;
    }
    try {
      let certificateOfIncorporationRef = "",
        taxIdProofRef = "",
        lastAuditedFinancialsRef = "";
      if (formData.certificateOfIncorporation instanceof File) {
        const result = await uploadFileToS3Path(
          formData.certificateOfIncorporation,
          bucketfolder
        );
        certificateOfIncorporationRef = result.fileName;
      }
      if (formData.taxIdProof instanceof File) {
        const result = await uploadFileToS3Path(
          formData.taxIdProof,
          bucketfolder
        );
        taxIdProofRef = result.fileName;
      }
      if (formData.lastAuditedFinancials instanceof File) {
        const result = await uploadFileToS3Path(
          formData.lastAuditedFinancials,
          bucketfolder
        );
        lastAuditedFinancialsRef = result.fileName;
      }
      const userKYBInput = {
        EmailID: activeUser,
        ContactNumber: formData.contactNumber,
        LegalBusinessName: formData.legalName,
        TradingName: formData.tradingName,
        BusinessType: formData.businessType,
        IndustrySector: formData.industrySector,
        CompanyRegistrationNumber: formData.regNumber,
        DateOfIncorporation: formData.incorporationDate
          ? dayjs(formData.incorporationDate).format("YYYY-MM-DD")
          : null,
        RegisteredAddress: formData.registeredAddress,
        OperationalAddress: formData.operationalAddress,
        CountryOfIncorporation: formData.country,
        TaxIdentificationNumber: formData.taxId,
        Website: formData.websiteEmail,
        AnnualTurnover: parseFloat(formData.annualTurnover) || 0,
        PaidUpCapital: parseFloat(formData.paidUpCapital) || 0,
        BankName: formData.bankName,
        BankAccountNumber: formData.accountNumber,
        IFSC_SwiftCode: formData.ifscSwift,
        SourceOfFunds: formData.sourceOfFunds,
        Status: formData.Status || "",
        Remarks: "",
        DateOfApproval: currentDate,
        DateOfSubmission: currentDate,
        certificateOfIncorporation: certificateOfIncorporationRef || "",
        taxIdProof: taxIdProofRef || "",
        lastAuditedFinancials: lastAuditedFinancialsRef || "",
      };
      if (isUpdateMode) {
        await postQuery({
          target: "updateUserKYB",
          userKYBInput: {
            ...userKYBInput,
            UserKybID: userKybID,
            Status: formData.Status || "Pending",
          },
        });
      } else {
        const newUserKybID = uuidv4();
        await postQuery({
          target: "createUserKYB",
          userKYBInput: {
            ...userKYBInput,
            UserKybID: newUserKybID,
            Status: "Pending",
          },
        });
        setUserKybID(newUserKybID);
        setIsUpdateMode(true);
      }
      setEditMode(false);
      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "KYB submitted successfully.",
        autoClose: false,
        nextAction: () => window.location.reload(),
      });
    } catch (error) {
      console.error("Error submitting KYB:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Failed to submit KYB. Please try again.",
        autoClose: false,
      });
    }
  };

  const renderFilePreview = (file, name) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "white",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        p: 1,
        mt: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {file instanceof File ? (
          file.name
        ) : (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${fileUrl}${file.name}`}
          >
            {file.name}
          </a>
        )}
      </Typography>
      <IconButton
        size="small"
        onClick={() => handleFileRemove(name)}
        disabled={readOnly}
        sx={{ ml: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        } else {
          setActiveUser(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
  }, [navigate]);

  useEffect(() => {
    loadAllDropdowns();
  }, [loadAllDropdowns]);
  useEffect(() => {
    if (activeUser) fetchExistingKYB();
  }, [activeUser, fetchExistingKYB]);

  const getStatusBadge = (status) => {
    let bgColor = "",
      textColor = "";
    switch (status) {
      case "Pending":
        bgColor = "#fef3c7";
        textColor = "#b45309";
        break;
      case "Approved":
      case "Verified":
        bgColor = "#d1fae5";
        textColor = "#065f46";
        break;
      default:
        bgColor = "#f3f4f6";
        textColor = "#374151";
    }
    return (
      <Box
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "0.75rem",
          fontWeight: 500,
          display: "inline-block",
        }}
      >
        {status === "Pending" ? "In Review" : status}
      </Box>
    );
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="container kyb-verification mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-3">
        <div
          className="table-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div className="header-text">
            <div className="head-text">KYB Verification</div>
            <div className="head-subtext" style={{ color: "#666" }}>
              Complete the following steps to verify your business.
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 10,
              gap: "10px",
            }}
          >
            {isUpdateMode &&
              formData.Status &&
              formData.Status !== "Approved" &&
              (!editMode ? (
                <button
                  className="submit-btn-more"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    className="submit-btn-more"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                  <button
                    className="submit-btn-more"
                    onClick={() => {
                      setEditMode(false);
                      fetchExistingKYB();
                    }}
                    disabled={loading}
                    style={{ backgroundColor: "#dc3545", color: "white" }}
                  >
                    Cancel
                  </button>
                </>
              ))}
            {!isUpdateMode && (
              <button
                className="submit-btn-more"
                onClick={handleSubmit}
                disabled={loading || readOnly}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            )}
          </div>
        </div>
        {isUpdateMode && formData.Status && (
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="d-flex justify-content-between align-items-center p-2"
                style={{
                  backgroundColor:
                    formData.Status === "Approved" ? "#d1fae573" : "#fef3c750",
                  color: formData.Status === "Approved" ? "#065f46" : "#b45309",
                  borderLeft:
                    formData.Status === "Approved"
                      ? "3px solid #00b658fb"
                      : "3px solid #b69203f7",
                  borderRadius: "5px",
                }}
              >
                <span
                  className="p-1 px-2 rounded"
                  style={{ fontSize: "0.875rem" }}
                >
                  Current Status: {getStatusBadge(formData.Status)}
                </span>
              </div>
            </div>
          </div>
        )}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, "& .MuiTabs-indicator": { display: "none" } }}
        >
          <Tab
            label="Company Information"
            sx={{
              borderBottom: "2px solid #de6b14 !important",
              "&:not(.Mui-selected)": { borderBottom: "none", border: "none" },
              "&.Mui-selected": {
                backgroundColor: "#de6b14 !important",
                color: "white",
                borderBottom: "2px solid #de6b14 !important",
                border: "none",
              },
            }}
          />
          <Tab
            label="Financial Information"
            sx={{
              borderBottom: "2px solid #de6b14 !important",
              "&:not(.Mui-selected)": { borderBottom: "none", border: "none" },
              "&.Mui-selected": {
                backgroundColor: "#de6b14 !important",
                color: "white",
                borderBottom: "2px solid #de6b14 !important",
                border: "none",
              },
            }}
          />
        </Tabs>
        {activeTab === 0 && (
          <Box className="tab-content">
            <div className="card">
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.legalName}>
                    <Typography variant="body2">
                      Legal Business Name *
                    </Typography>
                    <TextField
                      name="legalName"
                      value={formData.legalName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.legalName}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{
                        backgroundColor: readOnly ? "#f5f5f5" : "white",
                        marginTop: "2px",
                      }}
                      helperText={errors.legalName}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.tradingName}>
                    <Typography variant="body2" sx={{ margin: 0, padding: 0 }}>
                      <FormControlLabel
                        sx={{ margin: 0, padding: 0 }}
                        control={
                          <Checkbox
                            name="hasTradingName"
                            checked={formData.hasTradingName}
                            onChange={(e) => {
                              if (readOnly) return;
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                hasTradingName: checked,
                                tradingName: checked ? "" : prev.legalName,
                              }));
                              if (!checked)
                                setErrors((prev) => ({
                                  ...prev,
                                  tradingName: undefined,
                                }));
                            }}
                            disabled={readOnly}
                          />
                        }
                        label="Different Trading Name?"
                      />
                    </Typography>
                    <TextField
                      name="tradingName"
                      value={formData.tradingName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.tradingName}
                      disabled={readOnly || !formData.hasTradingName}
                      variant="outlined"
                      sx={{
                        backgroundColor:
                          readOnly || !formData.hasTradingName
                            ? "#f5f5f5"
                            : "white",
                        marginTop: "2px",
                      }}
                      helperText={errors.tradingName}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.businessType}>
                    <Typography variant="body2">Business Type *</Typography>
                    <Select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={readOnly}
                      displayEmpty
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    >
                      <MenuItem value="" disabled>
                        Select business type
                      </MenuItem>
                      {businessTypesMaster.map((bt) => (
                        <MenuItem key={bt.ID} value={bt.Value}>
                          {bt.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.businessType && (
                      <Typography variant="caption" color="error">
                        {errors.businessType}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.industrySector}>
                    <Typography variant="body2">Industry Sector *</Typography>
                    <Select
                      name="industrySector"
                      value={formData.industrySector}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={readOnly}
                      displayEmpty
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    >
                      <MenuItem value="" disabled>
                        Select industry sector
                      </MenuItem>
                      {industrySectorsMaster.map((is) => (
                        <MenuItem key={is.ID} value={is.Value}>
                          {is.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.industrySector && (
                      <Typography variant="caption" color="error">
                        {errors.industrySector}
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.regNumber}>
                    <Typography variant="body2">
                      Registration Number *
                    </Typography>
                    <TextField
                      name="regNumber"
                      value={formData.regNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.regNumber}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.regNumber}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.incorporationDate}>
                    <Typography variant="body2">
                      Date of Incorporation *
                    </Typography>
                    <TextField
                      name="incorporationDate"
                      type="date"
                      value={formData.incorporationDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.incorporationDate}
                      disabled={readOnly}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: today }}
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.incorporationDate}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.country}>
                    <Typography variant="body2">
                      Country of Incorporation *
                    </Typography>
                    <Select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={readOnly}
                      displayEmpty
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    >
                      <MenuItem value="" disabled>
                        Select country
                      </MenuItem>
                      {countrymaster.map((c) => (
                        <MenuItem key={c.ID} value={c.Value}>
                          {c.Value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.country && (
                      <Typography variant="caption" color="error">
                        {errors.country}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.taxId}>
                    <Typography variant="body2">
                      Tax Identification Number *
                    </Typography>
                    <TextField
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.taxId}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.taxId}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.registeredAddress}>
                    <Typography variant="body2">
                      Registered Address *
                    </Typography>
                    <TextField
                      name="registeredAddress"
                      value={formData.registeredAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.registeredAddress}
                      disabled={readOnly}
                      variant="outlined"
                      multiline
                      rows={2}
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.registeredAddress}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.operationalAddress}>
                    <Typography variant="body2" sx={{ margin: 0, padding: 0 }}>
                      <FormControlLabel
                        sx={{ margin: 0, padding: 0 }}
                        control={
                          <Checkbox
                            name="isOperationalDifferent"
                            checked={formData.isOperationalDifferent}
                            onChange={(e) => {
                              if (readOnly) return;
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                isOperationalDifferent: checked,
                                operationalAddress: checked
                                  ? ""
                                  : prev.registeredAddress,
                              }));
                              if (!checked)
                                setErrors((prev) => ({
                                  ...prev,
                                  operationalAddress: undefined,
                                }));
                            }}
                            disabled={readOnly}
                          />
                        }
                        label="Different Operational Address?"
                      />
                    </Typography>
                    <TextField
                      name="operationalAddress"
                      value={formData.operationalAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.operationalAddress}
                      disabled={readOnly || !formData.isOperationalDifferent}
                      variant="outlined"
                      multiline
                      rows={2}
                      sx={{
                        backgroundColor:
                          readOnly || !formData.isOperationalDifferent
                            ? "#f5f5f5"
                            : "white",
                      }}
                      helperText={errors.operationalAddress}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.websiteEmail}>
                    <Typography variant="body2">
                      Website / Official Email *
                    </Typography>
                    <TextField
                      name="websiteEmail"
                      value={formData.websiteEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.websiteEmail}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.websiteEmail}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.contactNumber}>
                    <Typography variant="body2">Contact Number *</Typography>
                    <TextField
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.contactNumber}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.contactNumber}
                    />
                  </FormControl>
                </div>
              </div>
            </div>
          </Box>
        )}
        {activeTab === 1 && (
          <Box className="tab-content">
            <div className="card">
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2">Annual Turnover</Typography>
                    <TextField
                      name="annualTurnover"
                      type="number"
                      value={formData.annualTurnover}
                      onChange={handleChange}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2">Paid Up Capital</Typography>
                    <TextField
                      name="paidUpCapital"
                      type="number"
                      value={formData.paidUpCapital}
                      onChange={handleChange}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.bankName}>
                    <Typography variant="body2">
                      Primary Operating Bank Name *
                    </Typography>
                    <TextField
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.bankName}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.bankName}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.accountNumber}>
                    <Typography variant="body2">
                      Bank Account Number *
                    </Typography>
                    <TextField
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.accountNumber}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.accountNumber}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-6">
                  <FormControl fullWidth error={!!errors.ifscSwift}>
                    <Typography variant="body2">IFSC / SWIFT Code *</Typography>
                    <TextField
                      name="ifscSwift"
                      value={formData.ifscSwift}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.ifscSwift}
                      disabled={readOnly}
                      variant="outlined"
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      helperText={errors.ifscSwift}
                    />
                  </FormControl>
                </div>
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2">Source of Funds</Typography>
                    <Select
                      name="sourceOfFunds"
                      value={formData.sourceOfFunds}
                      onChange={handleChange}
                      disabled={readOnly}
                      displayEmpty
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                    >
                      <MenuItem value="" disabled>
                        Select source of funds
                      </MenuItem>
                      {sourcesOfFundsMaster.map((sf) => (
                        <MenuItem key={sf.ID} value={sf.Value}>
                          {sf.Value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className="row" style={{ marginBottom: "10px" }}>
                <div className="col-md-12">
                  <FormControl fullWidth error={!!errors.verifyConfirm}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="verifyConfirm"
                          checked={formData.verifyConfirm}
                          onChange={handleChange}
                          disabled={readOnly}
                        />
                      }
                      label="I confirm that all information provided is accurate and complete."
                    />
                    {errors.verifyConfirm && (
                      <Typography variant="caption" color="error">
                        {errors.verifyConfirm}
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </div>
            </div>
          </Box>
        )}
      </div>
    </div>
  );
};

export default KYBVerification;
